import React, { useState, useEffect } from 'react';
import { X, Infinity, LogOut, Trash2, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { onAuthStateChanged, deleteUser, signOut } from 'firebase/auth';
import {
  isStripeBillingEnabled,
  getStripeProPaymentUrl,
} from '../config/stripe';
import { useSubscription } from '../hooks/useSubscription';
import './SettingsModal.css';

const SettingsModal = ({ isOpen, onClose, user: userProp }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const {
    loading: subscriptionLoading,
    isActive,
    isPro,
    isEnterprise,
  } = useSubscription();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => setUser(u));
    return () => unsubscribe();
  }, []);

  const open = isOpen !== false;
  if (!open) return null;

  const account = userProp || user;

  const deleteAccount = async () => {
    const u = account || auth.currentUser;
    if (!u) {
      alert('No user logged in');
      return;
    }

    const confirmed = window.confirm(
      'Are you sure you want to delete your account? This cannot be undone.'
    );
    if (!confirmed) return;

    try {
      setLoading(true);
      await deleteUser(u);
      alert('Account deleted successfully');
      window.location.href = '/';
    } catch (error) {
      console.error('Error deleting account:', error);
      alert(`Failed to delete account: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      setLoading(true);
      await signOut(auth);
      onClose();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
      alert(`Failed to sign out: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const isPaidSubscriber =
    !subscriptionLoading && isActive && (isPro || isEnterprise);

  const goProPricingPage = () => {
    onClose();
    navigate('/pricing#pricing-pro');
  };

  const handleUnlimitedNotes = () => {
    const uid = (account || auth.currentUser)?.uid;

    // Always send subscribers to in-app $9.99 Pro pricing (no second checkout tab).
    if (isPaidSubscriber) {
      goProPricingPage();
      return;
    }

    // While subscription is still loading, only open the pricing page (safe default).
    if (subscriptionLoading) {
      goProPricingPage();
      return;
    }

    if (isStripeBillingEnabled()) {
      const base = getStripeProPaymentUrl();
      const url = uid
        ? `${base}${base.includes('?') ? '&' : '?'}client_reference_id=${encodeURIComponent(uid)}`
        : base;
      window.open(url, '_blank', 'noopener,noreferrer');
      onClose();
      return;
    }

    goProPricingPage();
  };

  const unlimitedSubline = (() => {
    if (subscriptionLoading) return 'Pro — $9.99/mo';
    if (isPaidSubscriber) return 'View your plan ($9.99/mo Pro)';
    if (isStripeBillingEnabled()) return 'Pro checkout — $9.99/mo';
    return 'Pro plan — $9.99/mo on pricing page';
  })();

  return (
    <div className="settings-modal-overlay" onClick={onClose}>
      <div
        className="settings-modal-content settings-modal-compact"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="settings-modal-header settings-modal-header-simple">
          <h2 className="settings-modal-title">Settings</h2>
          <button type="button" className="settings-modal-close" onClick={onClose} aria-label="Close">
            <X size={22} />
          </button>
        </div>

        <div className="settings-modal-body settings-modal-body-menu">
          <ul className="settings-menu-list">
            <li>
              <button type="button" className="settings-menu-item" onClick={handleUnlimitedNotes}>
                <span className="settings-menu-icon settings-menu-icon-accent">
                  <Infinity size={20} strokeWidth={2.25} />
                </span>
                <span className="settings-menu-text">
                  <span className="settings-menu-label">Unlimited notes</span>
                  <span className="settings-menu-sub">{unlimitedSubline}</span>
                </span>
                <ChevronRight size={18} className="settings-menu-chevron" />
              </button>
            </li>
            <li>
              <button
                type="button"
                className="settings-menu-item"
                onClick={handleSignOut}
                disabled={loading}
              >
                <span className="settings-menu-icon">
                  <LogOut size={18} />
                </span>
                <span className="settings-menu-text">
                  <span className="settings-menu-label">Sign out</span>
                  {account?.email ? (
                    <span className="settings-menu-sub">{account.email}</span>
                  ) : null}
                </span>
                <ChevronRight size={18} className="settings-menu-chevron" />
              </button>
            </li>
            <li>
              <button
                type="button"
                className="settings-menu-item settings-menu-item-danger"
                onClick={deleteAccount}
                disabled={loading}
              >
                <span className="settings-menu-icon settings-menu-icon-danger">
                  <Trash2 size={18} />
                </span>
                <span className="settings-menu-text">
                  <span className="settings-menu-label">Delete account</span>
                </span>
                <ChevronRight size={18} className="settings-menu-chevron" />
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;
